/**
 * The list-print contract.
 *
 * Printing is the seventh list capability, and unlike the other six it needs
 * an implementation from the callsite: a CSV can be derived from `columns`
 * losslessly, a printed sheet cannot. So the capability is the switch and
 * this config is the implementation — the same split the list already makes
 * between `capabilities.select` (a view flag) and `resourceBulkActions` (a
 * host implementation).
 *
 * Everything here is data or a pure function. The React side lives in
 * `usePrintJob` / `PrintOptionsDialog`.
 */
import type { ReactNode } from 'react';

/**
 * Which rows to print. Deliberately the SAME two scopes the export dropdown
 * offers — an operator who learned one menu has learned both, and a third
 * scope ("the current page") was removed from export precisely because
 * nobody chooses it.
 */
export const PRINT_SCOPES = ['all', 'selected'] as const;
export type PrintScope = (typeof PRINT_SCOPES)[number];

/**
 * A print option, as a closed set of primitives rather than arbitrary JSX.
 *
 * The dialog stays generic, testable and consistent across resources only if
 * it knows every shape it can be asked to render. A callsite that needs a
 * control this cannot express is telling us the option belongs in the
 * rendered sheet, not in the dialog.
 *
 * One kind so far. The first callsite (the delivery-job printout) offers a
 * single "page-break per driver" toggle, which is also the only option its
 * predecessor page ever had. A `select` kind was drafted and dropped for
 * having no caller — the same judgement that kept `number` out. Adding one
 * back is a union member and a branch in the dialog.
 *
 * Labels are i18n KEYS, never literals — every user-visible string in this
 * app goes through i18next.
 */
export type PrintOptionDef = {
  kind: 'boolean';
  id: string;
  labelKey: string;
  default?: boolean;
};

/**
 * Widened past `boolean` on purpose: the option VALUES travel into
 * `buildSheet`, and narrowing them to the single kind the
 * dialog happens to render today would have to be widened again — through
 * every callsite — the first time a second kind appears.
 */
export type PrintOptionValue = string | boolean;
export type PrintOptionValues = Readonly<Record<string, PrintOptionValue>>;

/** What the renderer is handed once the operator has confirmed. */
export interface PrintSheetArgs<TRow> {
  /** The rows the scope resolved to. */
  rows: readonly TRow[];
  options: PrintOptionValues;
  scope: PrintScope;
}

/**
 * How many rows one print run may carry. A CSV of 10k rows is a file; 10k
 * rendered cards is a hung tab and a jammed printer, so the print ceiling is
 * far lower than {@link EXPORT_ALL_ROW_CAP} and is checked BEFORE any fetch.
 */
export const PRINT_ROW_CAP = 300;

export interface ListPrintConfig<TRow> {
  /**
   * Options offered before printing. Omitted or empty ⇒ no dialog at all:
   * confirming a form with nothing in it is a click that teaches nothing.
   */
  options?: readonly PrintOptionDef[];
  /**
   * Build the sheet for the chosen rows. May be async — a callsite whose
   * sheet needs data the list row does not carry fetches it here.
   *
   * ONE function rather than a `fetchPrintData` + `render` pair. The pair
   * needs a data type shared between them, and that type then has to
   * travel as a generic parameter through the host, the bundle, the page
   * and the list — four layers that never look at it. Closing it over in
   * the callsite costs nothing in expressiveness and removes the parameter
   * entirely.
   *
   * Omitted ⇒ the built-in table layout over the visible columns.
   */
  buildSheet?: (args: PrintSheetArgs<TRow>) => ReactNode | Promise<ReactNode>;
  /** `@page` descriptors. Defaults to US Letter portrait with 15/4/14mm margins. */
  page?: { size?: string; margin?: string };
  /**
   * Permission gating the print ACTION (not the data — the list route
   * already gates that). Absent ⇒ anyone who can see the list can print it.
   */
  permission?: string;
  /** Overrides {@link PRINT_ROW_CAP} for a resource with lighter rows. */
  rowCap?: number;
}

/** Default `@page` descriptors — US Letter, the printer these sheets target. */
export const DEFAULT_PRINT_PAGE = {
  size: 'letter portrait',
  margin: '15mm 4mm 14mm',
} as const;

/** Resolve a config's declared defaults into a starting value map. */
export function initialPrintOptions(
  defs: readonly PrintOptionDef[] | undefined,
): PrintOptionValues {
  const values: Record<string, PrintOptionValue> = {};
  for (const def of defs ?? []) {
    values[def.id] = def.default ?? false;
  }
  return values;
}
